// 管理后台的当前用户、登录与登出（docs/04 第 9 节，决策 D43）。
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";

/** 后台账号的角色，逐级包含：超级管理员比系统管理员多后台账号与审计日志，只读不能写（D181）。 */
export type Role = "super_admin" | "admin" | "viewer";

const ROLE_RANK: Record<Role, number> = { viewer: 0, admin: 1, super_admin: 2 };

/** role 是否不低于 min；还不知道角色时按只读处理。 */
export function roleAtLeast(role: Role | undefined, min: Role): boolean {
  return ROLE_RANK[role ?? "viewer"] >= ROLE_RANK[min];
}

/** 当前登录的后台账号。 */
export interface Me { id: number; username: string; role: Role }

export const ME_KEY = ["me"] as const;

/** 读当前用户；没有会话时是 null 而不是错误，路由守卫据此跳登录页。 */
async function fetchMe(): Promise<Me | null> {
  try {
    return await api<Me>("/auth/me");
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

/** 当前用户的查询。应用外壳用它决定是否放行。 */
export function useMe() {
  return useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: Infinity, retry: false });
}

/** 当前用户能否写入：只读账号不能。页面上只订阅缓存，不另发请求；还没有当前用户（例如单测直接渲染页面）时按可写处理，最终以服务端的 403 为准。 */
export function useCanWrite(): boolean {
  const { data } = useQuery({ queryKey: ME_KEY, queryFn: fetchMe, enabled: false });
  return data?.role !== "viewer";
}

/** 当前用户是不是超级管理员：只有它能用后台账号与审计日志（D181）。与 useCanWrite 同样只订阅缓存，还没有当前用户时按是处理，最终以服务端的 403 为准。 */
export function useIsSuperAdmin(): boolean {
  const { data } = useQuery({ queryKey: ME_KEY, queryFn: fetchMe, enabled: false });
  return !data || data.role === "super_admin";
}

/** 登录页渲染前要知道的设置：站点密钥为空表示不用人机验证（D173），环境标记为空表示不显示（D177）。 */
export interface AuthConfig { turnstile_site_key: string; env_label: string }

/** 读登录配置，不要求会话；读不到时按没启用人机验证渲染，服务端启用了会回 400 说明。 */
export function useAuthConfig() {
  return useQuery({ queryKey: ["auth-config"], queryFn: () => api<AuthConfig>("/auth/config"), staleTime: Infinity, retry: false });
}

/** 环境标记由 admin 的 AXON_ADMIN_ENV_LABEL 决定，同一个镜像在各环境通用；为空（正式环境）或读不到时不显示（D177）。
 * 也用来判断是不是正式环境：只给开发看的页面在正式环境隐藏（D179）。 */
export function useEnvLabel(): string {
  return useAuthConfig().data?.env_label ?? "";
}

/** 登录请求体；启用人机验证时带上 Turnstile 令牌。 */
export interface LoginBody { username: string; password: string; turnstile_token?: string }

/** 登录：成功后写入当前用户。 */
export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginBody) => api<Me>("/auth/login", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: (me) => qc.setQueryData(ME_KEY, me),
  });
}

/** 结束本地会话：当前用户置空，守卫随即回登录页；其余缓存全部删掉，上一个人看过的数据不留给下一个人。
 * 当前用户的查询不删只置空：删了它，已经订阅的外壳与守卫收不到变化。登出与任何接口回 401 都走这里。 */
export function endSession(qc: QueryClient) {
  qc.setQueryData(ME_KEY, null);
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
}

/** 登出：删服务端会话，无论成败都结束本地会话；失败原样抛给调用方提示。 */
export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    try {
      await api<void>("/auth/logout", { method: "POST" });
    } finally {
      endSession(qc);
    }
  };
}

/** 自助改密码的请求体（D201）。 */
export interface PasswordBody { current_password: string; new_password: string }

/** 改自己的密码：服务端成功后撤销该账号的全部会话，这里随即结束本地会话，守卫回登录页用新密码登录。
 * api 对 /auth/ 下的 401 不统一退出（那是登录接口的“用户名或密码错误”），这里的 401 只可能是会话已失效，自己结束会话。 */
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PasswordBody) => api<void>("/auth/password", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => endSession(qc),
    onError: (e) => { if (e instanceof ApiError && e.status === 401) endSession(qc); },
  });
}

/** 角色的中文名，从高到低，也是后台账号表单里选项的顺序。 */
export const ROLE_LABEL: Record<Role, string> = { super_admin: "超级管理员", admin: "系统管理员", viewer: "只读" };

const SAME_ORIGIN = "http://axon.invalid";

/** 登录后要回去的页面：只接受站内路径，防止 next 被拿来跳到外站。按浏览器的解析规则判断：
 * 反斜杠开头或夹着 Tab 的值前缀看着是站内，解析后却是外站，所以先拒绝反斜杠与控制字符，再核对解析出的源。 */
export function safeNext(next: string | null): string {
  if (!next || !next.startsWith("/") || [...next].some(unsafeChar)) return "/overview";
  const url = new URL(next, SAME_ORIGIN);
  if (url.origin !== SAME_ORIGIN || url.pathname.startsWith("/login")) return "/overview";
  return url.pathname + url.search + url.hash;
}

function unsafeChar(c: string): boolean {
  const code = c.charCodeAt(0);
  return c === "\\" || code < 0x20 || code === 0x7f;
}
